//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { ProductStatusMessage } from '@campaigns/components/shared/product-details-modal/details/ProductStatusMessage';
import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import { PRODUCT_ID_REGEX_RULE } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import {
  ProductModalMode,
  useProductModal,
} from '@campaigns/contexts/ProductModalContext';
import { useProductSearch } from '@campaigns/hooks/useProductSearch';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { uniqueProductIdValidator } from '@campaigns/utils/sampling/SamplingProductValidatorsUtils';
import { isDefined } from '@utils/filterPredicates';

interface ProductBasicFieldsProps {
  isViewOnly: boolean;
}

export const ProductBasicFields = ({ isViewOnly }: ProductBasicFieldsProps) => {
  const {
    productModalState,
    setModalMode,
    setShowImagePreview,
    updateProductInModalState,
  } = useProductModal();
  const { samplingCampaignRequest } = useSamplingCampaign();
  const {
    formState: { errors },
    getValues,
    reset,
  } = useFormContext<EditableBVSamplingProduct>();
  const [hasImportedProduct, setHasImportedProduct] = useState(false);
  const [productIdSearchText, setProductIdSearchText] = useState<
    string | undefined
  >(undefined);

  const isProductIdDisabled =
    !!productModalState.product?.targetCatalogProductId &&
    !productModalState.product?.newProduct;

  const existingProducts = samplingCampaignRequest?.products;

  const [debouncedProductId] = useDebounce(
    productIdSearchText,
    SEARCH_DEBOUNCE_DELAY,
  );

  const {
    data: catalogProducts,
    isFetched,
    isFetching: isCheckingCatalog,
  } = useProductSearch({
    productIds: debouncedProductId ? [debouncedProductId] : undefined,
    enabled: isDefined(debouncedProductId) && !isProductIdDisabled,
  });

  const handleSelectProduct = (catalogProduct: SamplingCatalogProduct) => {
    const currentValues = getValues();

    const transformedProduct: EditableBVSamplingProduct = {
      ...currentValues,
      name: catalogProduct.name,
      description: catalogProduct.description,
      brand: catalogProduct.brand,
      category: catalogProduct.category,
      imgUrl: catalogProduct.imageUrl,
      pdpUrl: catalogProduct.productDetailPageUrl,
      targetCatalogProductId: catalogProduct.id,
      newProduct: false,
      variations: [
        {
          fairMarketValue: catalogProduct.fmv
            ? catalogProduct.fmv.toString()
            : undefined,
          quantity: undefined,
          variantName: undefined,
          imgUrl: catalogProduct.imageUrl,
        },
      ],
    };

    const upcs = catalogProduct.upcs ?? [];
    const eans = catalogProduct.eans ?? [];
    const totalProductIdentifiers = upcs.length + eans.length;

    if (totalProductIdentifiers > 0 && transformedProduct.variations[0]) {
      if (totalProductIdentifiers === 1) {
        // If there's only one identifier, populate it to the GTIN/EAN/UPC field
        transformedProduct.variations[0].logisticsRef = upcs[0] ?? eans[0];
      } else {
        // If there are multiple identifiers, populate them to the GTIN/EAN/UPC for Syndication
        transformedProduct.destinationGTIN = upcs.concat(eans).join(',');
      }
    }

    reset(transformedProduct);

    // Trigger preview immediately for catalog imports
    if (catalogProduct.imageUrl) {
      setShowImagePreview(true);
    }

    setHasImportedProduct(true);
    setProductIdSearchText(undefined);

    updateProductInModalState(
      isDefined(catalogProduct.id) ? catalogProduct.id : '',
    );

    setModalMode(ProductModalMode.Edit);
  };

  const catalogProductData = catalogProducts?.products[0];

  const productIdError = errors.targetCatalogProductId?.message;

  const handleValueChange = (value: string) => {
    if (isProductIdDisabled || !value || !PRODUCT_ID_REGEX_RULE.test(value)) {
      return;
    }
    setProductIdSearchText(value);
    setHasImportedProduct(false);
  };

  return (
    <Container>
      <div>
        <FormField
          instructions={`Use the product's unique ID as it appears on your site or the unique identifier your team uses.`}
          isDisabled={isProductIdDisabled}
          isViewOnly={isViewOnly}
          label={ProductDetailsFieldLabels.targetCatalogProductId}
          labelHelpText={`Make sure the product ID doesn't start with a space or dash, and ends with a letter or number. 
            The product ID can only contain letters, numbers, underscores, periods, vertical bars, and dashes. 
            You can use an ID that exists in the catalog to create a new product, or modify imported catalog data as needed.`}
          name='targetCatalogProductId'
          onValueChange={handleValueChange}
          rules={{
            required: {
              value: true,
              message: 'Please enter a product ID',
            },
            pattern: {
              value: PRODUCT_ID_REGEX_RULE,
              message:
                'Product ID must not start with a space or dash, must end with a letter or number, and can only contain letters, numbers, underscores, periods, vertical bars, and dashes',
            },
            validate: (value: string) =>
              uniqueProductIdValidator(
                value,
                existingProducts ?? [],
                productModalState.product?.targetCatalogProductId,
              ),
          }}
          width='full'
        />

        {!isDefined(productIdError) && (isCheckingCatalog || isFetched) && (
          <ProductStatusMessage
            catalogProduct={catalogProductData}
            hasImportedProduct={hasImportedProduct}
            isCheckingCatalog={isCheckingCatalog}
            onSelectProduct={handleSelectProduct}
          />
        )}
      </div>

      <FormField
        inputType='text'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.name}
        labelHelpText='Make sure to list each product accurately: Body Wash, Mascara, Face Cream'
        name='name'
        placeholder=''
        rules={{
          required: {
            value: true,
            message: 'Please enter a product name',
          },
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
